/*
  ADA / WCAG 2.2 AA — runtime fixes for markup this theme cannot edit in Liquid:
  Shopify's own payment-button element, and the Judge.me review widget.

  Everything here only adds or removes ARIA and focus attributes. No third-party markup is
  restructured and no id or class another script depends on is changed, so the widgets
  keep working exactly as their own code expects.

  Contents:
    1. Price live region  — stop re-announcing an unchanged price on every variant click
    2. More payment options — stop the false "unavailable" while the checkout is built
    3. Judge.me histogram — don't expose zero-review rows as buttons; name the real ones
    4. Judge.me rating summary — role="img" must not trap a focusable link
    5. Size guide tables — scroll regions focusable only while they overflow
*/
(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * 1 · Price: announce the price only when it actually changed.
   *
   * sections/main-product.liquid puts role="status" on #price-{section}. Dawn's
   * VariantSelects.renderProductInfo() rewrites that node's innerHTML on every variant
   * change (global.js: `destination.innerHTML = source.innerHTML`), so the live region
   * fires even when every size costs the same — the owner heard the full price read out
   * again on each arrow key. A price change genuinely should be announced (4.1.3), so
   * rather than silence it we move the announcement to our own live region and only
   * speak when the text is different.
   *
   * If this script fails to load, the stock role="status" stays and behaviour is
   * unchanged — the fallback is the noisy-but-correct original.
   * ------------------------------------------------------------------ */
  function setupPriceAnnouncer() {
    var price = document.querySelector('[id^="price-"]');
    if (!price) return;

    // Text as a screen reader would get it: skip display:none branches (sale/unit price
    // blocks Dawn always renders) and the <style> tag Shopify injects inside the region.
    function visibleText(root) {
      var out = '';
      (function walk(node) {
        for (var i = 0; i < node.childNodes.length; i++) {
          var n = node.childNodes[i];
          if (n.nodeType === 3) {
            out += n.nodeValue;
          } else if (n.nodeType === 1) {
            if (n.tagName === 'STYLE' || n.tagName === 'SCRIPT') continue;
            var cs = window.getComputedStyle(n);
            if (cs.display === 'none' || cs.visibility === 'hidden' || n.hasAttribute('hidden')) continue;
            walk(n);
          }
        }
      })(root);
      return out.replace(/\s+/g, ' ').trim();
    }

    var last = visibleText(price);

    price.removeAttribute('role');

    var announcer = document.createElement('div');
    announcer.className = 'visually-hidden';
    announcer.setAttribute('role', 'status');
    announcer.setAttribute('aria-live', 'polite');
    price.parentNode.insertBefore(announcer, price.nextSibling);

    new MutationObserver(function () {
      // Dawn replaces innerHTML, so the node survives; re-strip role in case a
      // section re-render ever restores the attribute itself.
      price.removeAttribute('role');
      var now = visibleText(price);
      if (!now || now === last) return;
      last = now;
      announcer.textContent = now;
    }).observe(price, { childList: true, subtree: true, characterData: true });
  }

  /* ------------------------------------------------------------------ *
   * 2 · "More payment options": Shopify's <more-payment-options-link> sets
   * aria-disabled="true" on its inner <a> for ~600ms while it builds the checkout,
   * which NVDA reads as "unavailable" the instant the link is activated. The thing that
   * actually prevents a second activation is the `disabled` property on the custom
   * element plus the removal of href — neither of which is announced — so dropping the
   * aria-disabled changes nothing functionally and removes a false state (4.1.2).
   * ------------------------------------------------------------------ */
  function setupPaymentLink() {
    // Assert an explicit aria-disabled="false" rather than just removing a "true". An
    // explicit value on the element beats a disabled state inherited from an ancestor,
    // which is what finally fixes this — see the block comment in attach() below.
    function scrub(el) {
      if (el && el.getAttribute('aria-disabled') !== 'false') {
        Element.prototype.setAttribute.call(el, 'aria-disabled', 'false');
      }
    }

    function attach(link) {
      if (link.__adaPatched) return;
      link.__adaPatched = true;

      // A MutationObserver runs as a microtask *after* the attribute has already been
      // applied, so aria-disabled="true" was briefly real and NVDA still announced
      // "unavailable" (confirmed by logging setAttribute: the attribute lands, then our
      // observer removes it). Intercepting setAttribute on this one element stops it
      // from ever reaching the accessibility tree. The observer below stays as a
      // backstop in case Shopify ever sets the attribute by another route.
      // What actually causes the announcement, established by controlled experiment
      // against Chrome's real accessibility tree rather than by reading attributes:
      //
      //   parent gets native `disabled`        -> link NOT reported disabled (in Chrome)
      //   parent gets `aria-disabled="true"`   -> link IS reported disabled (inherited)
      //   parent disabled + link aria-disabled="false" -> link NOT reported disabled
      //
      // Shopify marks the parent <more-payment-options-link> disabled for ~1.1s while it
      // builds the checkout. Chrome doesn't propagate native disabled to descendants, but
      // other engines do, and an ancestor's aria-disabled propagates everywhere — which is
      // why patching only this link's own attributes never silenced it.
      //
      // An explicit value on the element beats anything inherited, so hold
      // aria-disabled="false" on the link permanently. This changes nothing functional:
      // the parent keeps its own `disabled`, so Shopify's state machine is untouched.
      // (Diverting the parent's attribute was tried and broke checkout outright.)
      var nativeSet = link.setAttribute;
      var nativeRemove = link.removeAttribute;

      link.setAttribute = function (name, value) {
        // Never let a "true" land; keep our explicit "false" in its place.
        if (String(name).toLowerCase() === 'aria-disabled') {
          return nativeSet.call(this, 'aria-disabled', 'false');
        }
        return nativeSet.call(this, name, value);
      };

      // The louder half of the problem: Shopify also strips the href for the duration,
      // while forcing role="link". An element exposed as a link with nothing to activate
      // is reported by NVDA as "unavailable" just as a disabled control is — so removing
      // aria-disabled alone was not enough. Keep the href so the link stays a genuine,
      // enabled link the whole time; the re-entrancy guard is reproduced below instead.
      link.removeAttribute = function (name) {
        var n = String(name).toLowerCase();
        if (n === 'href') return;
        if (n === 'aria-disabled') return; // our explicit "false" must stay put
        return nativeRemove.call(this, name);
      };

      // Shopify's own guard against a second activation was the missing href. Replicate
      // it without touching the accessibility tree: while their custom element is marked
      // disabled, a checkout is already being built, so swallow further activations.
      function checkoutInFlight() {
        var host = link.closest ? link.closest('more-payment-options-link') : null;
        return !!(host && host.hasAttribute('disabled'));
      }

      function guard(event) {
        if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
        if (!checkoutInFlight()) return; // first activation: let it through
        event.preventDefault();
        event.stopImmediatePropagation();
      }

      link.addEventListener('click', guard, true);
      link.addEventListener('keydown', guard, true);

      scrub(link);
      new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) scrub(records[i].target);
      }).observe(link, { attributes: true, attributeFilter: ['aria-disabled'] });
    }

    function attachAllWithin(root) {
      var links = root.querySelectorAll
        ? root.querySelectorAll('#more-payment-options-link, .shopify-payment-button__more-options')
        : [];
      for (var j = 0; j < links.length; j++) attach(links[j]);
      // The root itself can be the link (childList mutations report the node directly).
      if (root.matches && root.matches('#more-payment-options-link, .shopify-payment-button__more-options')) {
        attach(root);
      }
    }

    attachAllWithin(document);

    // The payment button renders late, re-renders on variant change, AND is rendered a
    // second time from scratch inside the quick-add modal on collection pages (the modal
    // injects a fresh copy of the whole product form via innerHTML — see quick-add.js
    // setInnerHTML). A single host found via querySelector() at setup time misses that
    // second copy entirely when the page starts out with no product form at all (any
    // collection or the homepage), so watch document.body instead: it exists on every
    // page and covers every place this widget can be injected, matching the pattern
    // already used for the wholesale form in ada-wholesale-fixes.js.
    if (!document.body) return;
    new MutationObserver(function (records) {
      for (var r = 0; r < records.length; r++) {
        var added = records[r].addedNodes;
        for (var k = 0; k < added.length; k++) {
          if (added[k].nodeType === 1) attachAllWithin(added[k]);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------ *
   * 3 · Judge.me rating histogram.
   *
   * Every row's .jdgm-histogram__star carries role="button" tabindex="0" with no text,
   * so each is (a) a button with no accessible name (4.1.2) and (b) focusable even on
   * rows with zero reviews, where Judge.me's own click handler does nothing — the owner
   * could tab to "1 star (0)" and press Enter to no effect, something a mouse user can
   * never do.
   *
   * Each row carries its own data-rating / data-frequency, so read those rather than
   * inferring the rating from row order: the list also contains a "See all reviews"
   * clear-filter row (data-rating="null") which is a real control and is left alone.
   * ------------------------------------------------------------------ */
  function fixHistogram(container) {
    var rows = container.querySelectorAll('.jdgm-histogram__row');
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];

      // The "See all reviews" row is a genuine control with its own label — skip it.
      if (row.classList.contains('jdgm-histogram__clear-filter')) continue;

      var star = row.querySelector('.jdgm-histogram__star');
      if (!star) continue;

      var stars = parseInt(row.getAttribute('data-rating'), 10);
      var count = parseInt(row.getAttribute('data-frequency'), 10);
      if (isNaN(count)) {
        var freqEl = row.querySelector('.jdgm-histogram__frequency');
        count = freqEl ? parseInt((freqEl.textContent || '').replace(/[^\d]/g, ''), 10) : NaN;
      }
      if (isNaN(stars) || isNaN(count)) continue;

      var starWord = stars === 1 ? 'star' : 'stars';
      var plural = count === 1 ? 'review' : 'reviews';

      // An earlier revision of this script labelled the row itself; clear that.
      row.removeAttribute('aria-label');

      if (count === 0) {
        // Not actionable with a mouse either, so take it out of the tab order and out of
        // the button role. role="img" + a label keeps the row meaningful to a screen
        // reader, which would otherwise hear only the bare number.
        star.removeAttribute('tabindex');
        star.setAttribute('role', 'img');
        star.setAttribute('aria-label', stars + ' ' + starWord + ': no reviews');
        star.setAttribute('data-ada-inert', 'true');
      } else {
        star.setAttribute('role', 'button');
        star.setAttribute('tabindex', '0');
        star.removeAttribute('data-ada-inert');
        star.setAttribute('aria-label', 'Show the ' + count + ' ' + plural + ' with ' + stars + ' ' + starWord);
      }
    }
  }

  /* ------------------------------------------------------------------ *
   * 4 · Judge.me rating summary: role="img" wrapping a focusable link.
   *
   * .jdgm-rev-widg__summary-stars is role="img" (labelled "Average rating is 5.00
   * stars") but contains <a class="jdgm-link">5.00 out of 5</a>. role="img" makes its
   * subtree presentational, so a focusable link inside is contradictory — axe flags it
   * as nested-interactive (4.1.2), and it was the last WCAG-tagged violation left on
   * the site.
   *
   * role="group" keeps the label exactly as it is and legitimately permits interactive
   * descendants, so nothing is removed and the link becomes properly reachable. Applied
   * by shape rather than by class name, so it also covers any other Judge.me image-role
   * container that ends up with focusable content.
   * ------------------------------------------------------------------ */
  function fixNestedInteractiveImg() {
    var candidates = document.querySelectorAll('[class*="jdgm"] [role="img"], [role="img"][class*="jdgm"]');
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      // No "already handled" guard: the selector only matches role="img", so a fixed
      // element drops out on its own. A guard would instead stop us re-fixing an element
      // whose role Judge.me resets to "img" on one of its re-renders.
      var focusable = el.querySelectorAll('a[href], button, input, select, textarea, [tabindex]');
      var hasFocusable = false;
      for (var j = 0; j < focusable.length; j++) {
        if (focusable[j].tabIndex >= 0) {
          hasFocusable = true;
          break;
        }
      }
      if (!hasFocusable) continue; // a plain star graphic: role="img" is correct, leave it

      el.setAttribute('role', 'group');
      el.setAttribute('data-ada-role-fixed', '1');
    }
  }

  function setupHistogram() {
    function run() {
      var widgets = document.querySelectorAll('.jdgm-histogram');
      for (var i = 0; i < widgets.length; i++) fixHistogram(widgets[i]);
      fixNestedInteractiveImg();
    }
    run();
    // Judge.me loads and re-renders asynchronously (and again when a filter is applied).
    var body = document.body;
    if (!body) return;
    var pending = null;
    new MutationObserver(function () {
      if (pending) return;
      pending = setTimeout(function () {
        pending = null;
        run();
      }, 300);
    }).observe(body, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------ *
   * 5 · Size guide tables: scrollable regions must be keyboard-reachable.
   *
   * On a narrow phone a size table can be wider than the dialog and scrolls sideways
   * inside .size-guide__table-wrap. A keyboard user can only scroll it if the region
   * can take focus (2.1.1; axe: scrollable-region-focusable). But a focusable region
   * that doesn't scroll is just a pointless extra tab stop, one per table — so add
   * tabindex only while the table actually overflows, and re-check on resize.
   * ------------------------------------------------------------------ */
  function setupSizeGuideScroll() {
    var wraps = document.querySelectorAll('.size-guide__table-wrap');
    if (!wraps.length) return;

    function check(wrap) {
      if (wrap.scrollWidth > wrap.clientWidth + 1) {
        wrap.setAttribute('tabindex', '0');
      } else {
        wrap.removeAttribute('tabindex');
      }
    }

    for (var i = 0; i < wraps.length; i++) check(wraps[i]);
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(function (entries) {
        for (var j = 0; j < entries.length; j++) check(entries[j].target);
      });
      for (var k = 0; k < wraps.length; k++) ro.observe(wraps[k]);
    } else {
      window.addEventListener('resize', function () {
        for (var m = 0; m < wraps.length; m++) check(wraps[m]);
      });
    }
  }

  function init() {
    try { setupPriceAnnouncer(); } catch (e) { /* never break the page over an a11y tweak */ }
    try { setupPaymentLink(); } catch (e) {}
    try { setupHistogram(); } catch (e) {}
    try { setupSizeGuideScroll(); } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
